/**
 * AS-11 (Ind AS 21) Year-End Restatement.
 *
 * Retranslates every open AR/AP in a foreign currency to the closing rate on
 * the reporting date (31-Mar), posting the diff to "Foreign Currency
 * Transactions Restatement Gain/Loss".
 *
 * Two-step:
 *   1. Preview — dry-run, shows old vs new INR, closing rate, per-doc diff
 *   2. Commit  — posts the JV, tags every doc with `restatement_run_id`
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertOctagon } from "lucide-react";

const fmtINR = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (v) => (v > 0 ? "+ " : v < 0 ? "- " : "") + "₹ " + Math.abs(Number(v || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AS11Restatement() {
  const defaultAsOf = () => {
    const t = new Date();
    // Default to 31 Mar of the last-completed FY (if we're past 1 April) else the previous 31 Mar
    const year = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
    return `${year}-03-31`;
  };
  const [asOf, setAsOf] = useState(defaultAsOf());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);

  const load = () => {
    setLoading(true);
    setCommitted(null);
    api.get("/reports/as11/preview", { params: { as_of: asOf } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [asOf]);

  const commit = async () => {
    if (!window.confirm(
      `Commit AS-11 restatement for ${asOf}?\n\n` +
      `Rows to restate: ${d?.row_count || 0}\n` +
      `Net gain/loss: ${fmtSigned(d?.net_gain_loss_inr || 0)} (${d?.net_direction})\n\n` +
      `This posts a JV to "Foreign Currency Transactions Restatement Gain/Loss" and ` +
      `updates every restated doc's balance to the closing rate. Cannot be undone.`
    )) return;
    setCommitting(true);
    try {
      const r = await api.post("/reports/as11/commit", { as_of: asOf });
      setCommitted(r.data);
      toast.success(`Restatement posted · voucher ${r.data.voucher_id?.slice(0,8)} · ${fmtSigned(r.data.net_gain_loss_inr)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    }
    setCommitting(false);
  };

  const canCommit = d && d.row_count > 0 && !d.already_posted;

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="as11-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// AS-11 · Ind AS 21</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">Year-End FX Restatement</h1>
          <div className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-3xl">
            Retranslate every open non-INR AR / AP balance to the closing rate on the
            reporting date. The FX diff is booked to <b>Foreign Currency Transactions
            Restatement Gain/Loss</b>. Preview first — commit only after CFO review.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Reporting date</Label>
          <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
            className="h-8 w-40 text-sm" data-testid="as11-as-of" />
          <Button onClick={load} disabled={loading}
            variant="outline" className="rounded-sm gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary */}
      {d && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="as11-summary">
          <div className="card-sharp p-4">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Rows to restate</div>
            <div className="text-2xl font-bold mt-1 font-mono">{d.row_count}</div>
          </div>
          <div className="card-sharp p-4">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">AR diff</div>
            <div className={`text-2xl font-bold mt-1 font-mono ${d.total_diff_ar > 0 ? "text-emerald-600" : d.total_diff_ar < 0 ? "text-red-500" : ""}`}>
              {fmtSigned(d.total_diff_ar)}
            </div>
          </div>
          <div className="card-sharp p-4">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">AP diff</div>
            <div className={`text-2xl font-bold mt-1 font-mono ${d.total_diff_ap < 0 ? "text-emerald-600" : d.total_diff_ap > 0 ? "text-red-500" : ""}`}>
              {fmtSigned(d.total_diff_ap)}
            </div>
          </div>
          <div className={`card-sharp p-4 border-l-4 ${d.net_direction === "gain" ? "border-l-emerald-500 bg-emerald-500/5" : d.net_direction === "loss" ? "border-l-red-500 bg-red-500/5" : "border-l-neutral-500"}`}>
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Net {d.net_direction}</div>
            <div className="text-2xl font-bold mt-1 font-mono">{fmtSigned(d.net_gain_loss_inr)}</div>
          </div>
        </div>
      )}

      {/* Closing rates panel */}
      {d && Object.keys(d.closing_rates || {}).length > 0 && (
        <div className="card-sharp p-3 flex items-center gap-4 flex-wrap text-xs" data-testid="as11-rates">
          <div className="font-mono uppercase text-[10px] tracking-widest text-accent">// closing rates on {d.as_of}</div>
          {Object.entries(d.closing_rates).map(([c, r]) => (
            <div key={c} className="font-mono">
              <b>{c}</b> = ₹ {Number(r).toFixed(4)}
            </div>
          ))}
        </div>
      )}

      {d?.already_posted && (
        <div className="card-sharp p-3 border-l-4 border-l-neutral-500 bg-neutral-500/5 flex items-start gap-2" data-testid="as11-already-posted">
          <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            Restatement for <b>{d.as_of}</b> has already been posted{d.previous_run ? ` on ${d.previous_run.committed_at?.slice(0,10)}` : ""}.
            Change the reporting date to run for a different close.
          </div>
        </div>
      )}
      {committed && (
        <div className="card-sharp p-3 border-l-4 border-l-emerald-500 bg-emerald-500/5 flex items-start gap-2" data-testid="as11-committed">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-700">
            <b>Restatement posted successfully.</b> Voucher {committed.voucher_id?.slice(0,8)} ·
            {" "}{committed.rows_restated} document(s) updated · net {fmtSigned(committed.net_gain_loss_inr)}.
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-sharp overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-2.5">Doc</th>
              <th className="text-left p-2.5">Party</th>
              <th className="text-center p-2.5">Kind</th>
              <th className="text-right p-2.5">Foreign Amt</th>
              <th className="text-right p-2.5">Old rate</th>
              <th className="text-right p-2.5">New rate</th>
              <th className="text-right p-2.5">Old INR</th>
              <th className="text-right p-2.5">New INR</th>
              <th className="text-right p-2.5">Diff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={9} className="text-center text-muted-foreground p-6">Loading…</td></tr>}
            {!loading && (d?.rows || []).length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted-foreground p-6 italic">No open non-INR balances · nothing to restate</td></tr>
            )}
            {(d?.rows || []).map((r, i) => (
              <tr key={r.doc_id} className="hover:bg-muted/30" data-testid={`as11-row-${i}`}>
                <td className="p-2.5"><div className="font-mono font-semibold">{r.doc_number}</div><div className="text-[10px] text-muted-foreground">{r.date}</div></td>
                <td className="p-2.5 font-medium">{r.party_name}</td>
                <td className="p-2.5 text-center text-[10px] font-mono uppercase">{r.kind}</td>
                <td className="p-2.5 text-right font-mono">{r.currency} {Number(r.balance_fx).toFixed(2)}</td>
                <td className="p-2.5 text-right font-mono text-muted-foreground">{Number(r.original_fx_rate).toFixed(4)}</td>
                <td className="p-2.5 text-right font-mono">{Number(r.closing_rate).toFixed(4)}</td>
                <td className="p-2.5 text-right font-mono text-muted-foreground">{fmtINR(r.balance_inr_current)}</td>
                <td className="p-2.5 text-right font-mono">{fmtINR(r.balance_inr_new)}</td>
                <td className={`p-2.5 text-right font-mono font-semibold ${r.diff_inr > 0 ? "text-emerald-600" : r.diff_inr < 0 ? "text-red-500" : ""}`}>{fmtSigned(r.diff_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={commit} disabled={!canCommit || committing}
          className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="as11-commit">
          {committing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {committing ? "Posting…" : `Commit AS-11 restatement · ${asOf}`}
        </Button>
      </div>
    </div>
  );
}
